import * as React from "react";
import {
  ExternalDropZone,
  Upload,
  UploadFileInfo,
  UploadOnAddEvent,
  UploadOnBeforeRemoveEvent,
  UploadOnBeforeUploadEvent,
  UploadOnProgressEvent,
  UploadOnRemoveEvent,
  UploadOnStatusChangeEvent,
} from "@progress/kendo-react-upload";
import styles from "./UploadPanel.module.scss";
import { useEffect, useState } from "react";
import { Button } from "@progress/kendo-react-buttons";
import { Popup } from "components";
import { PDFViewer } from "@progress/kendo-react-pdf-viewer";
import { SettingsContext } from "context";
import { v4 as uuidv4 } from "uuid";
import { PDFIcon } from "assets";
import { Error } from "@progress/kendo-react-labels";

const uploadRef = React.createRef<Upload>();

function upsert(array: any[], element: any) {
  const i = array.findIndex((_element) => _element?.uid === element?.uid);
  if (i > -1) array[i] = element; // (2)
  else array.push(element);
}

export const UploadPanel = (props: any) => {
  const [showResetBtn, setShowResetBtn] = useState(false);
  const [isVisible, setIsVisible] = useState(false);
  const [string, setString] = useState<any>("");
  const [disableSelectFiles, setDisableSelectFiles] = useState(false);
  const { industry } = React.useContext(SettingsContext);
  const [indexName, setIndexName] = useState("");
  const [containerName, setContainerName] = useState("");
  const [files, setFiles] = useState<UploadFileInfo[]>([]);

  useEffect(() => {
    setIndexName(`${uuidv4().split("-")[0]}-${Date.now()}`);
    setContainerName(`${uuidv4().split("-")[0]}-${Date.now()}`);
  }, []);

  function fileUpload(event: UploadOnStatusChangeEvent): void {
    setFiles(event.newState);
    if (event?.response?.response) {
      event.newState?.length === 3 && setShowResetBtn(true);
      props.setFileUploadResponse(event.response.response);
      setIndexName(event.response.response.index_name);
      setContainerName(event.response.response.container_name);
    }
  }

  const onAdd = (e: UploadOnAddEvent) => {
    if (e.affectedFiles.length) {
      const names = e.newState.map((obj) => obj.name);

      const duplicateNames = names.filter((name, index) => {
        return names.indexOf(name) !== index;
      });

      if (duplicateNames.length > 0) {
        alert("You cannot upload same file twice.");
        return setFiles((old) => old);
      }
    }
    if (e.affectedFiles.length + files.length > 3) {
      alert("You can only upload upto 3 pdf files.");
      return setFiles((old) => old);
    } else {
      const filteredFiles = e.affectedFiles
        .filter(
          (f) => f.extension !== ".pdf" || (f?.size && f.size >= 10240000)
        )
        .map((f) => f.uid);

      if (filteredFiles.length) {
        alert("You can only upload .pdf file(s) with size less than 10 MB.");
        return setFiles((old) =>
          old.filter((f) => !filteredFiles.includes(f.uid))
        );
      }

      return setFiles((old) => {
        return [...old, ...e.affectedFiles];
      });
    }
  };

  const onBeforeUpload = (e: UploadOnBeforeUploadEvent) => {
    e.additionalData.index_name = indexName;
    e.additionalData.container_name = containerName;

    return e;
  };

  const onRemove = (file: UploadFileInfo) => {
    setFiles((old) => old.filter((f) => f.uid !== file.uid));
    fetch(industry?.pdfUploadApi.split("/api")[0] + "/api/deleteDocument", {
      method: "post",
      body: JSON.stringify({
        index_name: indexName,
        file_name: file.name,
      }),
    })
      .then((res) => console.log(res))
      .catch((e) => console.log(e));
  };

  const onPreview = (file: UploadFileInfo) => {
    file
      ?.getRawFile?.()
      ?.arrayBuffer()
      .then((res) => {
        setString(res);
        setIsVisible(true);
      });
  };

  return (
    <div style={props?.style}>
      <Popup
        title="File Preview"
        showPopup={isVisible}
        onClose={() => setIsVisible(false)}
        className={styles.popup}
      >
        <PDFViewer
          style={{ width: "100%", height: "100%" }}
          arrayBuffer={string}
        />
      </Popup>
      {!props.hideExternalDropZone && !showResetBtn && (
        <ExternalDropZone
          uploadRef={uploadRef}
          disabled={files.length === 3}
          customHint={`Drag and Drop Here\nor\nBrowse Files\nYou can upload upto 3 pdf files.\n File size limit: 10 MB (per file)`}
          className={styles.externalDropZone}
        />
      )}
      <div style={{ height: "12px" }} />
      {files.length > 0 && files[0].status !== 4 && (
        <Error style={{ fontSize: 16, color: "#ffc000" }}>
          Please click on upload button to upload files.
        </Error>
      )}
      <div className={styles.uploadFeature}>
        <Upload
          className={`${styles.uploadArea} ${props?.className} ${
            props.className && styles.uploadDuringChatSelectHidden
          } ${
            (files.length === 3 || disableSelectFiles) &&
            styles.disableSelectFileBtn
          }`}
          autoUpload={false}
          onRemove={() => setFiles([])}
          listItemUI={(p) => {
            return (
              <div
                style={{
                  display: "flex",
                  flexDirection: "column",
                  width: "100%",
                  gap: 8,
                }}
              >
                {p.files.map((file) => (
                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: 8,
                      margin: "8px 0",
                      position: "relative",
                      height: 34,
                      width: "100%",
                    }}
                  >
                    <div style={{ width: 32, height: 32 }}>
                      <PDFIcon color={props.className && "white"} />
                    </div>
                    <div style={{ flex: 1 }}>
                      <div>{file.name}</div>
                      <div
                        style={{
                          color:
                            file.status === 4
                              ? "#37b400"
                              : file.status === 0
                              ? "#ff2a14"
                              : "black",
                        }}
                      >
                        {file.status === 4 && "File uploaded successfully."}
                        {file.status === 0 && "File upload failed."}
                      </div>
                    </div>
                    <div
                      className={`${styles.btnContainer} ${
                        props.className && styles.btnContainerInChat
                      }`}
                    >
                      <Button
                        onClick={() => onPreview(file)}
                        className={styles.previewBtn}
                      >
                        Preview File
                      </Button>
                      <span
                        onClick={() => onRemove(file)}
                        className={`k-icon k-i-close ${styles.removeBtn}`}
                      ></span>
                    </div>
                  </div>
                ))}
              </div>
            );
          }}
          ref={uploadRef}
          batch={false}
          multiple={true}
          files={files}
          onBeforeUpload={onBeforeUpload}
          removeField="file_name"
          onAdd={onAdd}
          withCredentials={false}
          restrictions={{
            maxFileSize: 10 * 1024 * 1000,
            allowedExtensions: ["pdf"],
          }}
          saveUrl={
            industry?.pdfUploadApi ??
            "https://pdfindexerfunction.azurewebsites.net/api/pdf"
          }
          saveField="pdf"
          onStatusChange={fileUpload}
        />
      </div>
    </div>
  );
};
